import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TableSortLabel,
  Avatar,
  alpha,
  Stack,
  useTheme,
  CircularProgress,
  IconButton,
  Collapse,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  Assignment,
  Person,
  Close as CloseIcon,
  CalendarToday,
  AttachMoney,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

import { Contract } from '../../types';
import { formatarData, formatarValor } from '../../utils/formatToView';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type OrdemCampo = 'id' | 'titulo' | 'data_contrato' | 'valor_contratado' | 'data_bloqueio' | 'data_cancelamento';
type OrdemDirecao = 'asc' | 'desc';

export interface ContratosTableProps {
  /** Lista de contratos a exibir */
  contratos: Contract[];
  /** Total de registros (para paginação) */
  totalRegistros?: number;
  /** Estado de loading */
  loading?: boolean;
  /** Erro ao buscar dados */
  error?: boolean;
  /** Se true, a tabela possui paginação e sorting controlados externamente */
  serverSide?: boolean;
  /** Paginação controlada */
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /** Ordenação controlada */
  orderBy?: OrdemCampo;
  order?: OrdemDirecao;
  onSort?: (campo: OrdemCampo) => void;
  /** Título opcional acima da tabela */
  titulo?: string;
  subtitulo?: string;
  /** Se true, mostra a tabela dentro de um Collapse com botão fechar */
  dismissible?: boolean;
  onClose?: () => void;
  open?: boolean;
  /** Cor do tema para a barra do header */
  headerColor?: string;
  /** Altura máxima da tabela */
  maxHeight?: number;
  /** Cor customizada para o texto do header */
  headerTextColor?: string;
  /** Indica se algo está sendo buscado de novo */
  isFetching?: boolean;
  /** IDs selecionados para edição em massa */
  selectedIds?: number[];
  /** Callback quando a seleção muda */
  onSelectionChange?: (ids: number[]) => void;
}

// ─── Chip de status ────────────────────────────────────────────────────────────
const statusConfig: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }
> = {
  ativo: { label: 'Ativo', color: 'success' },
  bloqueado: { label: 'Bloqueado', color: 'warning' },
  cancelado: { label: 'Cancelado', color: 'error' },
  aguardando_instalacao: { label: 'Ag. Instalação', color: 'info' },
  aguardando_assinatura: { label: 'Ag. Assinatura', color: 'default' },
  suspenso: { label: 'Suspenso', color: 'warning' },
};

// ─── Componente ───────────────────────────────────────────────────────────────

const ContratosTable: React.FC<ContratosTableProps> = ({
  contratos,
  totalRegistros,
  loading = false,
  error = false,
  serverSide = false,
  page = 0,
  rowsPerPage = 30,
  onPageChange,
  onRowsPerPageChange,
  orderBy = 'data_contrato',
  order = 'desc',
  onSort,
  titulo,
  subtitulo,
  dismissible = false,
  onClose,
  open = true,
  headerColor,
  headerTextColor,
  maxHeight,
  isFetching = false,
  selectedIds = [],
  onSelectionChange,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const total = totalRegistros ?? contratos.length;
  const corHeader = headerColor || theme.palette.primary.main;

  /** Sem `onSort`, a ordenação é controlada internamente (ex.: ContractsPage). */
  const sortExterno = typeof onSort === 'function';
  const [internalOrderBy, setInternalOrderBy] = React.useState<OrdemCampo>(orderBy);
  const [internalOrder, setInternalOrder] = React.useState<OrdemDirecao>(order);

  const sortField = sortExterno ? orderBy : internalOrderBy;
  const sortDir = sortExterno ? order : internalOrder;

  const camposDataDescDefault: OrdemCampo[] = ['data_contrato', 'data_bloqueio', 'data_cancelamento'];

  // Paginação / sort local quando não é server-side
  const displayContratos = React.useMemo(() => {
    if (serverSide) return contratos;
    let list = [...contratos];
    // sort
    list.sort((a: any, b: any) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (av === undefined || av === null) av = '';
      if (bv === undefined || bv === null) bv = '';

      if (sortField === 'id') {
        const na = typeof av === 'number' ? av : Number(String(av).replace(/\D/g, ''));
        const nb = typeof bv === 'number' ? bv : Number(String(bv).replace(/\D/g, ''));
        if (!Number.isNaN(na) && !Number.isNaN(nb)) {
          return sortDir === 'asc' ? na - nb : nb - na;
        }
      }

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const sa = String(av).toLowerCase();
      const sb = String(bv).toLowerCase();
      if (sa < sb) return sortDir === 'asc' ? -1 : 1;
      if (sa > sb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    // paginate
    return list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [contratos, serverSide, sortField, sortDir, page, rowsPerPage]);

  const showDataBloqueio = React.useMemo(() => contratos.some(c => c.status === 'bloqueado' || c.status === 'suspenso'), [contratos]);
  const showDataCancelamento = React.useMemo(() => contratos.some(c => c.status === 'cancelado'), [contratos]);
  const showEscritorio = React.useMemo(() => contratos.some(c => c.escritorio_id || c.escritorio), [contratos]);

  const handleSort = (campo: OrdemCampo) => {
    if (onSort) {
      onSort(campo);
      return;
    }
    setInternalOrderBy((prev) => {
      if (prev === campo) {
        setInternalOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setInternalOrder(camposDataDescDefault.includes(campo) ? 'desc' : 'asc');
      return campo;
    });
  };

  const selectedOnPage = displayContratos.filter(c => selectedIds.includes(c.id));
  const isAllSelected = displayContratos.length > 0 && selectedOnPage.length === displayContratos.length;
  const isSomeSelected = selectedOnPage.length > 0 && selectedOnPage.length < displayContratos.length;

  const handleSelectAll = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;

    if (event.target.checked) {
      const isMultiplePages = contratos.length > displayContratos.length;

      if (isMultiplePages) {
        const result = await Swal.fire({
          title: 'Selecionar Contratos',
          text: `Deseja selecionar apenas os ${displayContratos.length} contratos desta página ou todos os ${contratos.length} registros filtrados?`,
          icon: 'question',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: 'Todos os Registros',
          denyButtonText: 'Apenas esta Página',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: corHeader,
          denyButtonColor: alpha(corHeader, 0.7),
        });

        if (result.isConfirmed) {
          onSelectionChange(contratos.map(c => c.id));
        } else if (result.isDenied) {
          onSelectionChange(displayContratos.map(c => c.id));
        }
      } else {
        onSelectionChange(contratos.map(c => c.id));
      }
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: number) => {
    if (onSelectionChange) {
      const newSelection = selectedIds.includes(id)
        ? selectedIds.filter(sid => sid !== id)
        : [...selectedIds, id];
      onSelectionChange(newSelection);
    }
  };

  const tableContent = (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Header com título (quando dismissible) */}
      {(titulo || dismissible) && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: headerColor ? corHeader : alpha(corHeader, 0.06),
              color: headerTextColor || (headerColor ? 'white' : 'text.primary'),
              borderBottom: headerColor ? 'none' : `1px solid ${alpha(corHeader, 0.1)}`
            }}
          >
            <Box>
              {titulo && (
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'inherit'
                  }}
                >
                  {titulo}
                </Typography>
              )}
              {subtitulo && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    color: headerTextColor ? alpha(headerTextColor, 0.8) : (headerColor ? alpha('#fff', 0.8) : 'text.secondary')
                  }}
                >
                  {subtitulo}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(isFetching || loading) && <CircularProgress size={16} thickness={4} />}
              {dismissible && onClose && (
                <IconButton size="small" onClick={onClose} sx={{ bgcolor: alpha(theme.palette.error.main, 0.08) }}>
                  <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                </IconButton>
              )}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      <TableContainer sx={{ overflowX: 'auto', maxHeight: maxHeight ? maxHeight : 'none' }}>
        <Table size="small" stickyHeader={!!maxHeight} sx={{ minWidth: 800, '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${corHeader} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(corHeader, 0.2)}`,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <TableCell sx={{ width: 40, py: 0 }}>
                <Checkbox
                  size="small"
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              <TableCell sx={{ width: 70 }}>
                <TableSortLabel
                  active={sortField === 'id'}
                  direction={sortField === 'id' ? sortDir : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'titulo'}
                  direction={sortField === 'titulo' ? sortDir : 'asc'}
                  onClick={() => handleSort('titulo')}
                >
                  Título
                </TableSortLabel>
              </TableCell>
              <TableCell>Cliente</TableCell>
              {showEscritorio && <TableCell>Escritório</TableCell>}
              <TableCell align="right">Valor</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'data_contrato'}
                  direction={sortField === 'data_contrato' ? sortDir : 'desc'}
                  onClick={() => handleSort('data_contrato')}
                >
                  Data Contrato
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Vencimento</TableCell>
              {showDataBloqueio && (
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'data_bloqueio'}
                    direction={sortField === 'data_bloqueio' ? sortDir : 'desc'}
                    onClick={() => handleSort('data_bloqueio')}
                  >
                    Bloqueado em
                  </TableSortLabel>
                </TableCell>
              )}
              {showDataCancelamento && (
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'data_cancelamento'}
                    direction={sortField === 'data_cancelamento' ? sortDir : 'desc'}
                    onClick={() => handleSort('data_cancelamento')}
                  >
                    Cancelado em
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sx={{ width: 110 }}>Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {[1, 2, 3, (showEscritorio ? 4 : null), 5, 6, 7, (showDataBloqueio ? 8 : null), (showDataCancelamento ? 9 : null)].filter(Boolean).map((j, idx) => (
                    <TableCell key={idx} sx={{ py: 0.8 }}>
                      <Box
                        sx={{
                          height: 14,
                          borderRadius: 0.5,
                          bgcolor: alpha(theme.palette.text.disabled, 0.12),
                          width: j === 2 ? '80%' : j === 1 ? '40%' : '60%',
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8 + (showDataBloqueio ? 1 : 0) + (showDataCancelamento ? 1 : 0) + (showEscritorio ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="error">
                    Erro ao carregar contratos. Tente novamente.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : displayContratos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8 + (showDataBloqueio ? 1 : 0) + (showDataCancelamento ? 1 : 0) + (showEscritorio ? 1 : 0)} align="center" sx={{ py: 5 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.text.disabled, 0.1),
                        width: 48,
                        height: 48,
                      }}
                    >
                      <Assignment sx={{ color: 'text.disabled', fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Nenhum contrato encontrado
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              displayContratos.map((contract) => {
                const cfg = statusConfig[contract.status] ?? { label: contract.status, color: 'default' };
                return (
                  <TableRow
                    key={contract.id}
                    hover
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                    }}
                  >
                    <TableCell sx={{ py: 0, px: 1 }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(contract.id)}
                        onChange={() => handleSelectOne(contract.id)}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 1.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                      >
                        #{contract.id}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                        {contract.titulo}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Avatar sx={{ width: 22, height: 22, bgcolor: alpha(theme.palette.divider, 0.1), color: 'text.secondary' }}>
                          <Person sx={{ fontSize: 13 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {contract.cliente?.nome || '—'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {showEscritorio && (
                      <TableCell sx={{ py: 0.6, px: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                          {contract.escritorio?.identificacao || '—'}
                        </Typography>
                      </TableCell>
                    )}

                    <TableCell sx={{ py: 0.6, px: 1.5 }} align="right">
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color: 'success.main' }}>
                        {formatarValor(contract.valor_contratado || 0)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5 }} align="center">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {contract.data_contrato ? formatarData(contract.data_contrato) : '—'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5 }} align="center">
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        Dia {contract.dia_vencimento}
                      </Typography>
                    </TableCell>

                    {showDataBloqueio && (
                      <TableCell align="center" sx={{ py: 0.6, px: 1.5 }}>
                        <Typography variant="body2" color="warning.main" fontWeight={700} sx={{ fontSize: '0.78rem' }}>
                          {contract.data_bloqueio ? formatarData(contract.data_bloqueio) : '—'}
                        </Typography>
                      </TableCell>
                    )}

                    {showDataCancelamento && (
                      <TableCell align="center" sx={{ py: 0.6, px: 1.5 }}>
                        <Typography variant="body2" color="error.main" fontWeight={700} sx={{ fontSize: '0.78rem' }}>
                          {contract.data_cancelamento ? formatarData(contract.data_cancelamento) : '—'}
                        </Typography>
                      </TableCell>
                    )}

                    <TableCell sx={{ py: 0.6, px: 1.5 }}>
                      <Chip
                        label={cfg.label}
                        color={cfg.color}
                        size="small"
                        sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0.8,
          px: 2,
          bgcolor: alpha(corHeader, 0.04),
          borderTop: `1px solid ${alpha(corHeader, 0.15)}`,
          position: 'relative',
          flexWrap: 'nowrap',
          gap: 1,
        }}
      >
        <Box sx={{ position: { xs: 'static', md: 'absolute' }, left: 20, display: { xs: 'none', md: 'block' } }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {isFetching && !loading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CircularProgress size={11} thickness={5} />
                Atualizando...
              </span>
            ) : (
              <>
                Total: <strong>{total.toLocaleString('pt-BR')}</strong> contrato{total !== 1 ? 's' : ''}
              </>
            )}
          </Typography>
        </Box>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
          onPageChange={(_e, p) => onPageChange?.(p)}
          onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por pág:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count.toLocaleString('pt-BR') : `+${to}`}`
          }
          sx={{
            width: '100%',
            '.MuiTablePagination-toolbar': {
              minHeight: 32,
              flexWrap: 'nowrap',
              justifyContent: 'center',
              gap: 1,
              p: 0,
              '& .MuiTablePagination-spacer': { display: 'none' },
            },
            '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
            '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
            '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
            '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
          }}
        />
      </Box>
    </Paper>
  );

  // Se dismissible, envolve em Collapse
  if (dismissible) {
    return (
      <Collapse in={open} timeout={300} unmountOnExit>
        <Box sx={{ mt: 1.5, mb: 3 }}>
          {tableContent}
        </Box>
      </Collapse>
    );
  }

  return tableContent;
};

export default ContratosTable;
